import React, { memo, useContext, useMemo } from 'react';
import EDGrid from '../../../../../../BaseComponents/GridComponents/EDGrid';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import AttributeValue from '../../../../../../Components/Attributes/AttributeValue/AttributeValue';
import {
  IClientAttributeModel,
  IClientBusinessModel,
} from '../../../../../../Types/ClientModelTypes';
import EDListItemSecondaryAction from '../../../../../../BaseComponents/List/Components/EDListItemSecondaryAction';
import { IBusinessColorConfig } from '../../../Const/BusinessConfigs';
import { useIsExtraSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import { VisningsTypContext } from '../VisningsTypContext/VisningsTypContext';
import { GridContext } from '../../../../../GridContext';
import { ObjektVyKortDel } from '../ObjektVyKort/ObjektVyKortDel';
import { Breakpoint } from '../../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import LegendArea from '../../../../../../Components/Legend/LegendArea';
import { useWidth } from '../../../../../../Components/BreakpointContext/BreakpointContextUtils';

interface IDataListItem {
  name: string;
  value?: string;
}

export interface IDataListValue extends IDataListItem {
  attribute: IClientAttributeModel;
  hideLabel?: boolean;
  masked?: boolean;
}

interface IProps {
  collection: IClientBusinessModel;
  label: IDataListItem;
  values: IDataListValue[];
  numberOfColumns: number;
  listItemRightPadding: number;
  colors: IBusinessColorConfig[];
  secondaryAction?: React.ReactNode;
  inCard?: boolean;
  showCheckbox?: boolean;
  selected?: boolean;
  toggleSelected?: () => void;
  disabled?: boolean;
  caption?: string;
  disableNegativeMargin?: boolean;
  isNew?: boolean;
  miniList?: boolean;
}

const useStyles = makeStyles<IProps, 'gridItem' | 'itemContainer'>()(
  (theme: Theme, _params, classes) =>
    ({
      subtitle1: {},
      flexContainer: {
        display: 'flex',
      },
      griddedContainer: {
        boxSizing: 'border-box',
        padding: 0,
        lineHeight: 1.25,
        width: '100%',
        [`& p, & .${classes.itemContainer} p`]: {
          display: 'block',
          lineHeight: 1.2,
          margin: theme.spacing(0),
          padding: theme.spacing(0),
          verticalAlign: 'top',
          [theme.breakpoints.only('xs')]: {
            display: 'inline-block',
          },
        },
        '&:not(.MuiFormControl-root)': {
          padding: 0,
        },
        '&:empty': {
          display: 'none',
        },
        '& .MuiFormControl-root': {
          width: '100%',
        },
      },
      detailsRightGridded: {
        [`& .${classes.itemContainer} p`]: {
          display: 'inline-block',
        },
      },
      functionMenu: {
        opacity: 0,
        transition: 'opacity 100ms ease',
        [`.${classes.gridItem}:hover &`]: {
          opacity: 1,
        },
      },
      wrapText: {
        overflowY: 'auto',
        overflowWrap: 'break-word',
      },
      marginNone: {
        margin: '0',
        width: 'auto', // för att inte få bredder större Ã¤n 100%
      },
      negativeMargin: {
        margin: '0 -16px', // Får att bli av med fel höjdsättning av parent till grid container, se https://material-ui.com/components/grid/#negative-margin
      },
      negativeMarginInCard: {
        margin: '0 -8px',
      },
      negativeMarginListSmallScreen: {
        margin: 0,
      },
      gridItemsContainer: {
        [`&.${classes.gridItem}`]: {
          padding: 0,
          // [theme.breakpoints.only('xs')]: {
          //   padding: theme.spacing(0, 2),
          // },
        },
      },
      gridItemsContainerCardSmall: {
        [theme.breakpoints.only('xs')]: {
          '&[class*="MuiGrid-item"]': {
            padding: theme.spacing(0),
          },
        },
      },
      gridItem: {},
      outerContainer: {
        margin: theme.spacing(0),
        boxSizing: 'border-box',
        display: 'flex',
        flexFlow: 'row wrap',
        justifyContent: 'flex-start',
        [theme.breakpoints.only('xs')]: {
          flexDirection: 'column',
        },
      },
      itemContainer: {
        boxSizing: 'border-box',
        paddingTop: 0,
        paddingBottom: 0,
        paddingRight: 8,
        lineHeight: 1.2,
        flex: '0 1 auto',
        '& p': {
          display: 'inline-block',
          lineHeight: 1.2,
          margin: 0,
          padding: 0,
        },
        '& label': {
          fontWeight: 'bold',
        },
        // [theme.breakpoints.only('xs')]: {
        //   padding: '4px 0px',
        // },
        [theme.breakpoints.only('sm')]: {
          '& p + p': {
            wordBreak: 'break-word',
          },
        },
      },
      secondaryActionContainer: {
        right: 0,
      },
      caption: {
        color: theme.palette.text.primary,
        wordBreak: 'break-word',
        marginRight: theme.spacing(1.25),
      },
      textDisabled: {
        color: theme.palette.action.disabled,
      },
      legendContainer: {
        width: 36,
        marginTop: 12,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(1),
      },
      headerContainer: {
        display: 'flex',
        paddingTop: 0,
        flex: 1,
        width: '100%',
        '& [class*="MuiTypography-caption"], & [class*="MuiTypography-subtitle2"]':
          {
            lineHeight: 1.25,
          },
      },
      legendBox: {
        display: 'inline-block',
      },
    } as const)
);

const getNumberOfColumns = (
  numberOfFields: number,
  maxNumberOfColumns: number
): number => {
  return numberOfFields
    ? Math.ceil(numberOfFields / Math.ceil(numberOfFields / maxNumberOfColumns))
    : 1;
};

const getWidthPercentage = (
  numberOfFields: number,
  maxNumberOfColumns: number
): number => {
  const numberOfColumns = getNumberOfColumns(
    numberOfFields,
    maxNumberOfColumns
  );

  return Math.floor(100 / numberOfColumns);
};

const getMaxColumnsFromBreakpoint = (
  breakpoint: Breakpoint,
  inCard: boolean
): number => {
  let maxColumns = 0;
  if (breakpoint === 'xl') {
    maxColumns = inCard ? 3 : 4;
  } else if (breakpoint === 'lg') {
    maxColumns = inCard ? 2 : 3;
  } else if (breakpoint === 'md') {
    maxColumns = 2;
  } else if (breakpoint === 'sm') {
    maxColumns = 2;
  } else if (breakpoint === 'xs') {
    maxColumns = 1;
  }

  return maxColumns;
};

const BusinessDataListView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles(props, {
    props: props as any,
  });
  const currentWidth = useWidth();
  const visningsTypContext = useContext(VisningsTypContext);
  const maxColumns = getMaxColumnsFromBreakpoint(currentWidth, !!props.inCard);
  const isExtraSmallScreen = useIsExtraSmallScreen();

  const numberOfColumns = getNumberOfColumns(props.numberOfColumns, maxColumns);
  const widthPercentage = getWidthPercentage(props.numberOfColumns, maxColumns);
  const headerColWidth = Math.round(12 / (numberOfColumns + 1)) as
    | 1
    | 2
    | 3
    | 4; // Bredd på headern när den ska visas som en kolumn
  const nonHeaderColWidth = (12 - headerColWidth) as 1 | 2 | 3 | 4;
  const gridContext = useContext(GridContext);

  const header = (
    <span className={classes.flexContainer}>
      {props.showCheckbox && props.toggleSelected && (
        <EDCheckbox
          checked={props.selected}
          onCheck={props.toggleSelected}
          noClickPropagation
        />
      )}
      <div className={classes.headerContainer}>
        <div
          style={{
            flex: 1,
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <EDTypography
            variant="subtitle2"
            classes={{
              root: cx(classes.subtitle1, classes.caption, {
                [classes.textDisabled]: props.disabled || props.isNew,
              }),
            }}
            bold
          >
            {props.label.value}
          </EDTypography>
        </div>
      </div>
    </span>
  );

  const values = useMemo(
    () => (
      <>
        {props.values.flatMap((value, index) =>
          props.miniList && value.value === ''
            ? []
            : [
                <div
                  key={index}
                  style={{ width: `${widthPercentage}%` }}
                  className={cx(classes.itemContainer)}
                >
                  <span className={classes.flexContainer}>
                    <AttributeValue
                      collection={props.collection}
                      attribute={value.attribute}
                      value={value.value}
                      label={value.name}
                      disabled={props.disabled || props.isNew}
                      fillContainer
                      hideLabel={value.hideLabel}
                      masked={value.masked}
                      noLeftMargin
                      topMargin={false}
                      smallText={props.miniList}
                    />
                  </span>
                </div>,
              ]
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.values,
      props.collection,
      props.disabled,
      props.isNew,
      widthPercentage,
      classes.itemContainer,
      classes.flexContainer,
      isExtraSmallScreen,
    ]
  );

  return (
    // FIXME: villkora in på gridded
    <>
      {gridContext.inGrid && (
        <ObjektVyKortDel>
          <div
            className={cx(classes.griddedContainer, {
              [classes.detailsRightGridded]:
                visningsTypContext.showDetailsRight,
            })}
          >
            {header}
          </div>
          <div
            className={cx(classes.griddedContainer, {
              [classes.detailsRightGridded]:
                visningsTypContext.showDetailsRight,
            })}
          >
            {values}
          </div>
        </ObjektVyKortDel>
      )}
      {!gridContext.inGrid && (
        <EDBox sx={{ display: 'flex', width: '100%' }}>
          <EDBox sx={{ flexBasis: '8px', flexShrink: 0 }}>
            <LegendArea legendData={props.colors} />
          </EDBox>
          <EDBox
            sx={{
              flexGrow: 1,
              paddingLeft: 0.5,
              paddingTop: 1,
            }}
          >
            <EDGrid container spacing={0}>
              <EDGrid
                key="header"
                xs={12}
                sm={props.inCard ? 12 : headerColWidth}
                md={props.inCard ? 12 : headerColWidth}
                lg={props.inCard ? 12 : headerColWidth}
                xl={headerColWidth}
                item
              >
                {header}
              </EDGrid>
              <EDGrid
                xs={12}
                sm={props.inCard ? 12 : nonHeaderColWidth}
                md={props.inCard ? 12 : nonHeaderColWidth}
                lg={props.inCard ? 12 : nonHeaderColWidth}
                xl={nonHeaderColWidth}
                classes={{
                  root: props.inCard
                    ? classes.gridItemsContainerCardSmall
                    : classes.gridItemsContainer,
                  item: classes.gridItem,
                }}
                item
              >
                <div className={classes.outerContainer}>{values}</div>
              </EDGrid>
              <EDListItemSecondaryAction
                classes={{ root: classes.secondaryActionContainer }}
              >
                {props.secondaryAction}
              </EDListItemSecondaryAction>
            </EDGrid>
          </EDBox>
        </EDBox>
      )}
    </>
  );
};

export default memo(BusinessDataListView);
